import type { FunctionComponent, PropsWithChildren } from 'react';
import Table, { RowSubcomponent } from '@common/components/table/Table';
import { PageLayout } from '@common/components/PageLayout';
import { useBackendTable } from '@common/components/table/hooks';
import { useQuery } from '@tanstack/react-query';
import DendreoLog from '@spraypaint/dendreo/DendreoLog';
import { createColumnHelper } from '@tanstack/react-table';
import { ModelRecord } from 'spraypaint/lib-esm/model';
import dayjs from 'dayjs';
import {
  getMultiSelectFilterFn,
  multiSelectFilterWithOptions,
} from '@common/components/table/filter/MultiSelectFilter';
import {
  DateFilter,
  getDateFilterFn,
} from '@common/components/table/filter/DateFilter';
import { ButtonPrimary } from '@common/components/buttons';
import { SearchIcon } from '@/app/assets/icons/buttons';
import { TimeAgo } from '@/app/components/TimeAgo';
import { useUrlTableStateManager } from '@common/components/table/hooks/useUrlTableStateManager';

const ch = createColumnHelper<ModelRecord<DendreoLog>>();
const columns = [
  ch.accessor('id', { header: 'ID', size: 10 }),
  ch.accessor('title', { header: 'Titre' }),
  ch.accessor('status', {
    header: 'Status',
    filterFn: getMultiSelectFilterFn(),
    meta: {
      filter: {
        Component: multiSelectFilterWithOptions([
          { value: 'pending', label: 'En cours' },
          { value: 'ignored', label: 'Ignoré' },
          { value: 'error', label: 'Erreur' },
          { value: 'success', label: 'Succès' },
        ]),
      },
    },
  }),
  ch.accessor('source', { header: 'source' }),
  ch.accessor('timestamp', {
    cell: ({ getValue }) => <TimeAgo date={getValue()} />,
    header: 'Timestamp',
    filterFn: getDateFilterFn(),
    meta: {
      filter: {
        Component: DateFilter,
      },
    },
  }),
];

const Section: FunctionComponent<
  PropsWithChildren<{ title: string; displayed: boolean }>
> = ({ displayed, title, children }) => {
  if (!displayed) return null;
  return (
    <div className="flex flex-col text-gray-500">
      <h2 className="text-md font-bold">{title}</h2>
      <pre className="text-sm text-gray-500 bg-gray-100 p-2 rounded-md ml-4">
        {children}
      </pre>
    </div>
  );
};

const SubComponent: RowSubcomponent<ModelRecord<DendreoLog>> = ({ row }) => {
  const { payload, message, trace } = row.original;

  return (
    <div className="pl-12">
      <Section title="Message" displayed={!!message}>
        {message}
      </Section>{' '}
      <Section title="Trace" displayed={!!trace}>
        {trace}
      </Section>{' '}
      <Section title="Payload" displayed={!!payload}>
        {JSON.stringify(payload, null, 2)}
      </Section>
    </div>
  );
};

export const DendreoLogsIndex: FunctionComponent = () => {
  const [scope, scopeKey, reactTableProps] = useBackendTable<DendreoLog>({
    tableStateManager: useUrlTableStateManager('logs'),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['DendreoLogs', scopeKey],
    queryFn: () => scope(DendreoLog.order({ timestamp: 'desc' })).all(),
  });

  const totalCount = data?.meta.stats.total.count;

  return (
    <PageLayout
      Title="Logs Dendreo"
      Buttons={
        <ButtonPrimary to="../invoices" className="mr-2">
          Factures
        </ButtonPrimary>
      }
    >
      {' '}
      <Table
        data={data?.data ?? []}
        columns={columns}
        totalCount={totalCount}
        isLoading={isLoading}
        reactTableProps={reactTableProps}
        onRowClick="expand"
        tableStyle="compact"
        RowSubcomponent={SubComponent}
      />{' '}
    </PageLayout>
  );
};
